import { ButtonLink } from "@ui/ButtonLink";
import { MaterialSymbol } from "@ui/MaterialSymbol";
import type { QuestionBoxHandle } from "@ui/organisms/QuestionBox/QuestionBox";
import type { QuestionContextVideo } from "@ui/organisms/QuestionBox/QuestionContextVideos";
import type { AcceptedEffortUpgrade } from "@ui/organisms/QuestionBox/useUpgradableEffort";
import { Text } from "@ui/typography/Text";
import { createSignal, type JSX, Match, Show, Switch } from "solid-js";
import { JoinButton } from "~/modules/askgvt/ask-page/components/JoinButton";
import { useAskPageJoin } from "~/modules/askgvt/ask-page/useAskPageJoin";
import { signInUrl } from "~/modules/shared/utils/auth";
import { AskPageForm } from "./AskPageForm";
import type { AskedQuestion } from "./useAskPageForm";
import "./AskPageFollowUp.css";

type Props = {
	handle: string;
	influencerId: string;
	displayName: string;
	/** The question a follow-up is asked from. */
	threadRootId: string;
	/** Videos the thread has mentioned, which a follow-up can mention. */
	contextVideos: QuestionContextVideo[];
	isSignedIn: boolean;
	/** Whether the signed-in visitor's own credits pay for a follow-up. */
	paysOwnCredits?: boolean;
	/** False when only the creator's subscribers (or team) may follow up. */
	canFollowUp: boolean;
	/** Set while an answer in the thread is still being written. */
	disabled?: boolean;
	/** Runs just before a guest leaves for sign-in. */
	onSignIn?: () => void;
	/** Receives the composer, once there is one: a locked bar has none. */
	onReady?: (handle: QuestionBoxHandle) => void;
	/** Given, a follow-up is shown in place rather than navigated to. */
	onAsked?: (asked: AskedQuestion) => void;
	/** The latest effort upgrade accepted in the thread, which follow-ups carry on at. */
	acceptedEffortUpgrade?: AcceptedEffortUpgrade;
};

/**
 * The follow-up composer at the foot of a thread. Anyone who can't follow up
 * gets a locked bar in its place saying why, and what to do about it.
 */
export const AskPageFollowUp = (props: Props) => {
	const [subscribedHere, setSubscribedHere] = createSignal(false);

	return (
		<Switch
			fallback={
				<AskPageForm
					handle={props.handle}
					influencerId={props.influencerId}
					threadRootId={props.threadRootId}
					placeholder="Ask a follow-up..."
					variant="reply"
					maxLines={10}
					disabled={props.disabled}
					isSignedIn={props.isSignedIn}
					paysOwnCredits={props.paysOwnCredits}
					creatorName={props.displayName}
					contextVideos={props.contextVideos}
					onReady={props.onReady}
					onAsked={props.onAsked}
					acceptedEffortUpgrade={props.acceptedEffortUpgrade}
				/>
			}
		>
			<Match when={!props.isSignedIn && !props.canFollowUp}>
				<FollowUpGate message="Sign in to ask a follow-up question.">
					<ButtonLink
						class="ask-page-follow-up-gate__cta"
						href={signInUrl()}
						onClick={() => props.onSignIn?.()}
						variant="primary"
						size="medium"
						symbol="login"
						symbolPosition="after"
					>
						Sign in
					</ButtonLink>
				</FollowUpGate>
			</Match>
			<Match when={!props.canFollowUp && !subscribedHere()}>
				<SubscribeGate
					handle={props.handle}
					displayName={props.displayName}
					onSubscribed={() => setSubscribedHere(true)}
				/>
			</Match>
		</Switch>
	);
};

/**
 * The creator AskPage's own join button: a free creator is subscribed to in
 * place, a paid one sends the visitor to the AskPage to pick a plan.
 */
const SubscribeGate = (props: {
	handle: string;
	displayName: string;
	onSubscribed: () => void;
}) => {
	const { join, busy, error } = useAskPageJoin({
		displayName: props.displayName,
		onPaidJoin: () => {
			window.location.href = `/@${props.handle}`;
		},
		onFreeJoined: () => props.onSubscribed(),
	});

	return (
		<>
			<FollowUpGate message="Subscribe to this creator to ask a follow-up question.">
				<JoinButton onJoin={() => void join()} joinBusy={busy} />
			</FollowUpGate>
			<Show when={error()}>
				<Text as="p" size="small" color="error">
					{error()}
				</Text>
			</Show>
		</>
	);
};

const FollowUpGate = (props: { message: string; children: JSX.Element }) => (
	<div class="ask-page-follow-up-gate">
		<MaterialSymbol
			symbol="lock"
			class="ask-page-follow-up-gate__icon"
			aria-hidden
		/>
		<p class="ask-page-follow-up-gate__message">{props.message}</p>
		{props.children}
	</div>
);
